import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";

import messages from "../../../../../assets/locale/messages";
import { UIActionModal, UIDatePicker, UITimeField } from "../../../../ui";
import { TIMESHEET_SLOT_TABS } from "../constants";
import {
  ADD_SLOT_FORM_FIELDS,
  EMPTY_ADD_SLOT_FORM,
  getAddSlotDatePickerProps,
  hasAddSlotErrors,
  isAddSlotSaveDisabled,
  mapSlotRowToFormValues,
  validateAddSlotForm,
} from "./addSlotFormHelpers";

import "./AddSlotModal.scss";

const SLOT_MODAL_MODES = {
  add: "add",
  edit: "edit",
};

const AddSlotModal = ({
  open,
  onClose,
  mode,
  slot,
  applicantName,
  applicant,
  slotType,
  event,
  loading,
  onSave,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, inputsValidations } = messages[lang];
  const copy = messages[lang].events.viewEvents.timesheetTab.addSlotModal;
  const [values, setValues] = useState(EMPTY_ADD_SLOT_FORM);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const isEditMode = mode === SLOT_MODAL_MODES.edit;

  useEffect(() => {
    if (!open) {
      setValues(EMPTY_ADD_SLOT_FORM);
      setTouched({});
      setSubmitAttempted(false);
      return;
    }

    setValues(
      isEditMode && slot ? mapSlotRowToFormValues(slot) : EMPTY_ADD_SLOT_FORM,
    );
    setTouched({});
    setSubmitAttempted(false);
  }, [isEditMode, open, slot]);

  const title = useMemo(() => {
    if (isEditMode) {
      return slotType === TIMESHEET_SLOT_TABS.trainings
        ? copy.editTrainingTitle
        : copy.editShiftTitle;
    }

    return slotType === TIMESHEET_SLOT_TABS.trainings
      ? copy.trainingTitle
      : copy.shiftTitle;
  }, [copy, isEditMode, slotType]);

  const subtitle = (isEditMode ? copy.editSubtitle : copy.subtitle).replace(
    "{name}",
    applicantName || "-",
  );
  const validationContext = useMemo(
    () => ({
      slotType,
      applicant,
      originalSlot: isEditMode ? slot : null,
    }),
    [applicant, isEditMode, slot, slotType],
  );
  const errors = useMemo(
    () => validateAddSlotForm(values, event, validationContext),
    [event, validationContext, values],
  );
  const saveDisabled =
    isAddSlotSaveDisabled(values, event, validationContext) || loading;
  const {
    calendarKey: checkInCalendarKey,
    ...checkInDatePickerProps
  } = useMemo(
    () => getAddSlotDatePickerProps(event, values.checkInDate, slotType),
    [event, slotType, values.checkInDate],
  );
  const {
    calendarKey: checkOutCalendarKey,
    ...checkOutDatePickerProps
  } = useMemo(
    () =>
      getAddSlotDatePickerProps(
        event,
        values.checkOutDate || values.checkInDate,
        slotType,
      ),
    [event, slotType, values.checkInDate, values.checkOutDate],
  );

  const resolveFieldError = (field) => {
    if (!(touched[field] || submitAttempted) || !errors[field]) {
      return undefined;
    }

    return inputsValidations[errors[field]] || errors[field];
  };

  const markTouched = (field) => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  // Cross-field rules (checkout vs checkin date/time, shift margins) can block Save
  // based on a field the user didn't directly edit. Touch every field on any change
  // so a save-blocking error is never left invisible.
  const updateField = (field) => (nextValue) => {
    setTouched((current) => ({
      ...current,
      ...ADD_SLOT_FORM_FIELDS.reduce((acc, f) => ({ ...acc, [f]: true }), {}),
    }));
    setValues((current) => ({ ...current, [field]: nextValue || "" }));
  };

  const handleSave = () => {
    setSubmitAttempted(true);
    setTouched(
      ADD_SLOT_FORM_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field]: true }),
        {},
      ),
    );

    const nextErrors = validateAddSlotForm(values, event, validationContext);
    if (hasAddSlotErrors(nextErrors)) {
      return;
    }

    onSave?.(values, { mode, slot });
  };

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={title}
      variant="info"
      iconName="alertCircle"
      maxWidth="sm"
      PaperProps={{ className: "add-slot-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={copy.saveChanges}
          confirmDisabled={saveDisabled}
          onCancel={onClose}
          onConfirm={handleSave}
        />
      }
    >
      <Typography className="add-slot-modal__subtitle ui-action-modal__body-text">
        {subtitle}
      </Typography>
      <div className="add-slot-modal__grid">
        <UIDatePicker
          key={
            open
              ? `checkInDate-${checkInCalendarKey}`
              : "checkInDate-closed"
          }
          name="checkInDate"
          label={copy.checkInDate}
          required
          value={values.checkInDate}
          error={Boolean(resolveFieldError("checkInDate"))}
          helperText={resolveFieldError("checkInDate")}
          onBlur={() => markTouched("checkInDate")}
          onChange={updateField("checkInDate")}
          {...checkInDatePickerProps}
        />
        <UITimeField
          name="checkInTime"
          label={copy.checkInTime}
          required
          value={values.checkInTime}
          error={Boolean(resolveFieldError("checkInTime"))}
          helperText={resolveFieldError("checkInTime")}
          onBlur={() => markTouched("checkInTime")}
          onChange={updateField("checkInTime")}
        />
        <UIDatePicker
          key={
            open
              ? `checkOutDate-${checkOutCalendarKey}`
              : "checkOutDate-closed"
          }
          name="checkOutDate"
          label={copy.checkOutDate}
          value={values.checkOutDate}
          error={Boolean(resolveFieldError("checkOutDate"))}
          helperText={resolveFieldError("checkOutDate")}
          onBlur={() => markTouched("checkOutDate")}
          onChange={updateField("checkOutDate")}
          {...checkOutDatePickerProps}
        />
        <UITimeField
          name="checkOutTime"
          label={copy.checkOutTime}
          value={values.checkOutTime}
          error={Boolean(resolveFieldError("checkOutTime"))}
          helperText={resolveFieldError("checkOutTime")}
          onBlur={() => markTouched("checkOutTime")}
          onChange={updateField("checkOutTime")}
        />
      </div>
    </UIActionModal>
  );
};

AddSlotModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(Object.values(SLOT_MODAL_MODES)),
  slot: PropTypes.object,
  applicantName: PropTypes.string,
  applicant: PropTypes.object,
  slotType: PropTypes.oneOf([
    TIMESHEET_SLOT_TABS.shifts,
    TIMESHEET_SLOT_TABS.trainings,
  ]),
  event: PropTypes.object,
  loading: PropTypes.bool,
  onSave: PropTypes.func,
};

AddSlotModal.defaultProps = {
  mode: SLOT_MODAL_MODES.add,
  slot: null,
  applicantName: "",
  applicant: null,
  slotType: TIMESHEET_SLOT_TABS.shifts,
  event: null,
  loading: false,
  onSave: undefined,
};

export default AddSlotModal;
